import { useState, useEffect } from 'react';
import { useLazyQuery } from '@apollo/client';
import { Link } from '@reach/router';
import { useAuth } from '@calefy-inc/authentication';
import { ArrayElement } from '@calefy-inc/utilityTypes';
import ButtonGroup from '@mui/material/ButtonGroup';
import Button from '@mui/material/Button';
import Collapse from '@mui/material/Collapse';
import Typography from '@mui/material/Typography';
import { Quote } from '../../../../../../Typescript/backend/classes';
import { GetAllQuoteEditionsQuery } from '../../../../../../gql/graphql';
import { isNotNullish, sortByFriendlyId } from '../../../../../../util';
import { GET_ALL_QUOTE_EDITIONS } from '../../../../../../queries';
import { styled } from '@mui/material';

interface QuoteEditionsDisplayProps {
  quote?: Quote;
}

type QuoteEdition = ArrayElement<
  NonNullable<GetAllQuoteEditionsQuery['getAllQuoteEditions']>
>;

export const QuoteEditionsDisplay = ({ quote }: QuoteEditionsDisplayProps) => {
  const { token } = useAuth();
  const [quoteEditions, setQuoteEditions] = useState<Array<QuoteEdition>>([]);
  const [getAllQuoteEditionsQuery, { data, loading }] = useLazyQuery(
    GET_ALL_QUOTE_EDITIONS,
  );

  useEffect(() => {
    if (quote?.uniqueId && token) {
      getAllQuoteEditionsQuery({
        variables: {
          token,
          quoteUuid: quote.uniqueId,
        },
      });
    }
  }, [quote?.uniqueId, token, getAllQuoteEditionsQuery]);

  useEffect(() => {
    if (data?.getAllQuoteEditions) {
      const filteredEditions = [...data.getAllQuoteEditions].filter(
        isNotNullish,
      );
      const editions = filteredEditions.sort(sortByFriendlyId);
      setQuoteEditions(editions);
    }
  }, [data]);

  return (
    <Collapse
      in={
        // the current quote will always be an edition -> there will be a minimum of 1
        !loading && quoteEditions.length > 1
      }
    >
      <EditionsContainer>
        <Typography variant='h5' component='h3'>
          Editions
        </Typography>
        <StyledButtonGroup variant='outlined' aria-label='quote editions'>
          {quoteEditions.map((edition, index) => {
            const isCurrentQuote = edition.uniqueID === quote?.uniqueId;
            const buttonText = edition.friendlyId || `Edition ${index + 1}`;

            return (
              <Button
                key={edition.id}
                component={Link}
                to={`/insurtech/manager/quotes/details/${edition.uniqueID}`}
                disabled={isCurrentQuote}
                variant={isCurrentQuote ? 'contained' : 'outlined'}
                color={isCurrentQuote ? 'primary' : 'inherit'}
              >
                {buttonText}
              </Button>
            );
          })}
        </StyledButtonGroup>
      </EditionsContainer>
    </Collapse>
  );
};

const StyledButtonGroup = styled(ButtonGroup)(({ theme }) => {
  return {
    padding: theme.spacing(2, 0),
  };
});

const EditionsContainer = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
});
